import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { UserManagement } from '@/components/UserManagement';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, Loader2, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_enabled: true,
    notify_30_days: true,
    notify_7_days: true,
    notify_1_day: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/notifications/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <h1 className="text-4xl font-black font-heading tracking-tight mb-2" data-testid="settings-title">
              Settings
            </h1>
            <p className="text-muted-foreground font-body">
              Manage your account preferences
            </p>
          </div>

          <Tabs defaultValue="notifications" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
              <TabsTrigger value="users">User Management</TabsTrigger>
            </TabsList>

            <TabsContent value="notifications">
              <Card className="border-border/50 backdrop-blur-sm" data-testid="notification-settings-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/30">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-heading text-xl">Email Notifications</CardTitle>
                  <CardDescription className="font-body">
                    Get notified before your domains expire
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-enabled" className="text-sm font-medium font-body">
                    Enable Email Notifications
                  </Label>
                  <p className="text-sm text-muted-foreground font-body">
                    Receive email alerts for domain expirations
                  </p>
                </div>
                <Switch
                  id="email-enabled"
                  checked={settings.email_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
                  data-testid="email-enabled-switch"
                />
              </div>

              <div className="border-t border-border/50 pt-6 space-y-4">
                <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
                  Alert Thresholds
                </h3>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="notify-30" className="text-sm font-medium font-body">
                      30 Days Before Expiry
                    </Label>
                    <p className="text-sm text-muted-foreground font-body">
                      Early warning notification
                    </p>
                  </div>
                  <Switch
                    id="notify-30"
                    checked={settings.notify_30_days}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_30_days: checked })}
                    disabled={!settings.email_enabled}
                    data-testid="notify-30-days-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="notify-7" className="text-sm font-medium font-body">
                      7 Days Before Expiry
                    </Label>
                    <p className="text-sm text-muted-foreground font-body">
                      Urgent reminder notification
                    </p>
                  </div>
                  <Switch
                    id="notify-7"
                    checked={settings.notify_7_days}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_7_days: checked })}
                    disabled={!settings.email_enabled}
                    data-testid="notify-7-days-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="notify-1" className="text-sm font-medium font-body">
                      1 Day Before Expiry
                    </Label>
                    <p className="text-sm text-muted-foreground font-body">
                      Final warning notification
                    </p>
                  </div>
                  <Switch
                    id="notify-1"
                    checked={settings.notify_1_day}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_1_day: checked })}
                    disabled={!settings.email_enabled}
                    data-testid="notify-1-day-switch"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all duration-300"
                  data-testid="save-settings-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6 border-border/50 backdrop-blur-sm" data-testid="api-keys-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl">API Configuration</CardTitle>
              <CardDescription className="font-body">
                Configure your WHOIS and email API keys in the backend .env file
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">WhoisXML API Key</p>
                <p className="text-sm font-mono text-muted-foreground">WHOIS_API_KEY in /app/backend/.env</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">SendGrid API Key</p>
                <p className="text-sm font-mono text-muted-foreground">SENDGRID_API_KEY in /app/backend/.env</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Sender Email</p>
                <p className="text-sm font-mono text-muted-foreground">SENDER_EMAIL in /app/backend/.env</p>
              </div>
            </CardContent>
          </Card>
            </TabsContent>

            <TabsContent value="users">
              <UserManagement />
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}